import React, { useEffect, useMemo, useState } from "react";
import styles from "./Promotion.module.css";

import PageLayout from "../../components/PageLayout/PageLayout";
import PaymentModal from "../../stuffs/PaymentModal/PaymentModal";
import Spinner from "../../stuffs/Spinner/Spinner";

import {
  Button,
  FormControlLabel,
  Radio,
  RadioGroup,
} from "@mui/material";

import {
  NavLink,
  Redirect,
  useHistory,
  useParams,
} from "react-router-dom";

import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import moment from "moment";

import {
  addPromotionUser,
  CompanyType,
  fetchUserCompanies,
  selectCompanies,
} from "../../slices/company/companySlice";

import { selectIsLogIn } from "../../slices/auth/authSlice";
import { AppDispatch, RootState } from "../../app/store";

type PromotionType = "highlighted" | "recommended";

interface RecommendedAvailabilityResponse {
  available: boolean;
  currentCount: number;
  availableSlots: number;
}

const Promotion: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const history = useHistory();

  const { id } = useParams<{ id: string }>();

  const isLogIn = useSelector(selectIsLogIn);
  const companies: CompanyType[] = useSelector(selectCompanies);

  const isLoading = useSelector(
    (state: RootState) => state.companies.isLoading
  );

  const companyError = useSelector(
    (state: RootState) => state.companies.error
  );

  const [hasTriedFetch, setHasTriedFetch] = useState(false);

  const [promotionType, setPromotionType] =
    useState<PromotionType>("highlighted");

  const [showPaymentModal, setShowPaymentModal] =
    useState(false);

  const [isCheckingRecommended, setIsCheckingRecommended] =
    useState(false);

  const [recommendedAvailable, setRecommendedAvailable] =
    useState(false);

  const [recommendedMessage, setRecommendedMessage] =
    useState("");

  const [recommendedError, setRecommendedError] =
    useState("");

  const userEmail = useMemo(() => {
    const userString = localStorage.getItem("user");

    if (!userString) {
      return null;
    }

    try {
      const parsedUser = JSON.parse(userString);

      return parsedUser?.email || null;
    } catch {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!userEmail || hasTriedFetch) {
      return;
    }

    setHasTriedFetch(true);
    dispatch(fetchUserCompanies());
  }, [dispatch, userEmail, hasTriedFetch]);

  const company = useMemo(() => {
    return companies.find(
      (item) => String(item._id) === String(id)
    );
  }, [companies, id]);

  /*
   * Promocja obowiązuje do końca obecnego okresu oferty.
   * Płatność liczona jest za każdy rozpoczęty rok.
   */
  const promotionPeriod = useMemo(() => {
    if (!company?.expirationDate) {
      return 1;
    }

    const startDate = moment();
    const expirationDate = moment(company.expirationDate);

    if (
      !expirationDate.isValid() ||
      expirationDate.isSameOrBefore(startDate, "day")
    ) {
      return 1;
    }

    const remainingYears = expirationDate.diff(
      startDate,
      "years",
      true
    );

    return Math.max(1, Math.ceil(remainingYears));
  }, [company]);

  /*
   * Cena na froncie służy tylko do wyświetlenia.
   * Backend powinien wyliczyć ją ponownie z konfiguracji.
   */
  const promotionPrice = useMemo(() => {
    const pricePerYear =
      promotionType === "recommended" ? 300 : 150;

    return promotionPeriod * pricePerYear;
  }, [promotionPeriod, promotionType]);

  const paymentValues = useMemo(() => {
    if (!company) {
      return null;
    }

    return {
      _id: company._id,
      promotionType,
      newPeriod: promotionPeriod,

      isHighlighted: true,
      isRecommended: promotionType === "recommended",

      NIP: company.NIP,
      name: company.name,
      shortName: company.shortName || company.name,

      street: company.street,
      streetNumber: company.streetNumber,
      localNumber: company.localNumber,
      zipCode: company.zipCode,
      city: company.city,
    };
  }, [company, promotionPeriod, promotionType]);

  const checkRecommendedAvailability =
    async (): Promise<boolean> => {
      if (!company) {
        return false;
      }

      const mainField = company.mainField?.[0];
      const voivodeship = company.voivodeship;

      if (!mainField || !voivodeship) {
        setRecommendedAvailable(false);

        setRecommendedError(
          "Brak rodzaju pojazdu lub województwa potrzebnego do sprawdzenia TOP 3."
        );

        return false;
      }

      setIsCheckingRecommended(true);
      setRecommendedAvailable(false);
      setRecommendedMessage("");
      setRecommendedError("");

      try {
        const response =
          await axios.post<RecommendedAvailabilityResponse>(
            "/api/offers/recommended-availability",
            {
              mainField,
              voivodeship,
              offerId: company._id,
            },
            {
              headers: {
                "Content-Type": "application/json",
                "x-auth-token":
                  localStorage.getItem("token") || "",
              },
            }
          );

        if (!response.data.available) {
          setRecommendedError(
            "Brak wolnych miejsc TOP 3 dla tej kategorii i województwa."
          );

          return false;
        }

        setRecommendedAvailable(true);

        setRecommendedMessage(
          `Dostępne miejsca TOP 3: ${response.data.availableSlots}`
        );

        return true;
      } catch (error) {
        if (axios.isAxiosError(error)) {
          setRecommendedError(
            error.response?.data?.error ||
              "Nie udało się sprawdzić dostępności TOP 3."
          );
        } else {
          setRecommendedError(
            "Nie udało się sprawdzić dostępności TOP 3."
          );
        }

        return false;
      } finally {
        setIsCheckingRecommended(false);
      }
    };

  const handlePromotionChange = async (
    selectedType: PromotionType
  ) => {
    setRecommendedError("");
    setRecommendedMessage("");

    if (selectedType === "highlighted") {
      setPromotionType("highlighted");
      setRecommendedAvailable(false);

      return;
    }

    const available =
      await checkRecommendedAvailability();

    if (available) {
      setPromotionType("recommended");
    } else {
      setPromotionType("highlighted");
    }
  };

  const handleOpenPaymentModal = async () => {
    if (promotionType === "recommended") {
      const available =
        recommendedAvailable ||
        (await checkRecommendedAvailability());

      if (!available) {
        return;
      }
    }

    setShowPaymentModal(true);
  };

const handleSubmitPromotion = async () => {
  if (!company?._id) {
    return;
  }

  try {
    await dispatch(
      addPromotionUser({
        _id: company._id,
        promotionType,
      })
    ).unwrap();

    setShowPaymentModal(false);
    history.push("/my-bills");
  } catch (error) {
    console.error(
      "Nie udało się zamówić promocji:",
      error
    );
  }
};

  if (!isLogIn || !userEmail) {
    return <Redirect to="/signin" />;
  }

  if (!hasTriedFetch || isLoading) {
    return <Spinner />;
  }

  if (!company || !paymentValues) {
    return (
      <PageLayout className={styles.Promotion}>
        <div className={styles.MessageBox}>
          <h1>Nie znaleziono firmy</h1>

          <p>
            Nie udało się odnaleźć firmy przypisanej do
            wskazanego adresu.
          </p>

          <NavLink
            to="/my-offers"
            className={styles.BackLink}
          >
            Wróć do moich firm
          </NavLink>
        </div>
      </PageLayout>
    );
  }

  const alreadyHighlighted =
    (
      !!company.highlightedExpirationDate &&
      new Date(company.highlightedExpirationDate) > new Date()
    ) ||
    company.addHighlighted;

  const alreadyRecommended =
    (
      !!company.recommendedExpirationDate &&
      new Date(company.recommendedExpirationDate) > new Date()
    ) ||
    company.addRecommended;
    
  return (
    <PageLayout className={styles.Promotion}>
      <div className={styles.Header}>
        <div>
          <h1 className={styles.Title}>
            Promuj firmę
          </h1>

          <p className={styles.CompanyName}>
            {company.name}
          </p>
        </div>

        <NavLink
          to="/my-offers"
          className={styles.BackLink}
        >
          Wróć do moich firm
        </NavLink>
      </div>

      <div className={styles.InfoBox}>
        <h2>Wybierz rodzaj promocji</h2>

        <p>
          Wyróżnienie umieszcza wizytówkę nad ofertami
          standardowymi. Polecana TOP 3 zapewnia miejsce
          wśród trzech polecanych firm i zawiera również
          wyróżnienie.
        </p>
      </div>

      {companyError && (
        <div className={styles.Error}>
          {companyError}
        </div>
      )}

      <div className={styles.Options}>
        <RadioGroup
          value={promotionType}
          onChange={(_, value) =>
            handlePromotionChange(
              value as PromotionType
            )
          }
        >
          <div
            className={`${styles.Option} ${
              promotionType === "highlighted"
                ? styles.SelectedOption
                : ""
            }`}
          >
            <FormControlLabel
              value="highlighted"
              control={<Radio />}
              disabled={alreadyHighlighted}
              label={
                <div>
                  <strong>Wyróżnienie</strong>

                  <div className={styles.OptionDescription}>
                    150 PLN netto za każdy rozpoczęty rok
                  </div>
                </div>
              }
            />

            {alreadyHighlighted && (
              <div className={styles.OptionStatus}>
                Oferta jest już wyróżniona lub oczekuje na
                wyróżnienie.
              </div>
            )}
          </div>

          <div
            className={`${styles.Option} ${
              promotionType === "recommended"
                ? styles.SelectedOption
                : ""
            }`}
          >
            <FormControlLabel
              value="recommended"
              control={<Radio />}
              disabled={
                alreadyRecommended ||
                isCheckingRecommended
              }
              label={
                <div>
                  <strong>Polecana TOP 3</strong>

                  <div className={styles.OptionDescription}>
                    300 PLN netto za każdy rozpoczęty rok.
                    Zawiera wyróżnienie.
                  </div>
                </div>
              }
            />

            {alreadyRecommended && (
              <div className={styles.OptionStatus}>
                Oferta jest już polecana lub oczekuje na
                polecenie.
              </div>
            )}
          </div>
        </RadioGroup>
      </div>

      {isCheckingRecommended && (
        <div className={styles.Info}>
          Sprawdzanie dostępności TOP 3...
        </div>
      )}

      {recommendedMessage && (
        <div className={styles.Success}>
          {recommendedMessage}
        </div>
      )}

      {recommendedError && (
        <div className={styles.Error}>
          {recommendedError}
        </div>
      )}

      <div className={styles.Summary}>
        <h2>Podsumowanie</h2>

        <div className={styles.SummaryRow}>
          <span>Firma</span>
          <strong>{company.name}</strong>
        </div>

        <div className={styles.SummaryRow}>
          <span>Promocja</span>

          <strong>
            {promotionType === "recommended"
              ? "Polecana TOP 3"
              : "Wyróżnienie"}
          </strong>
        </div>

        <div className={styles.SummaryRow}>
          <span>Okres rozliczenia</span>

          <strong>
            {promotionPeriod}{" "}
            {promotionPeriod === 1
              ? "rozpoczęty rok"
              : "rozpoczęte lata"}
          </strong>
        </div>

        <div className={styles.SummaryRow}>
          <span>Cena netto</span>

          <strong>
            {promotionPrice.toFixed(2)} PLN
          </strong>
        </div>

        <div className={styles.SummaryRow}>
          <span>Cena brutto</span>

          <strong>
            {(promotionPrice * 1.23).toFixed(2)} PLN
          </strong>
        </div>
      </div>

      <div className={styles.Actions}>
        <Button
          type="button"
          variant="outlined"
          onClick={handleOpenPaymentModal}
          disabled={
            isCheckingRecommended ||
            (promotionType === "highlighted" &&
              alreadyHighlighted) ||
            (promotionType === "recommended" &&
              alreadyRecommended)
          }
        >
          {isCheckingRecommended
            ? "SPRAWDZANIE..."
            : "ZAMÓW PROMOCJĘ"}
        </Button>
      </div>

      <PaymentModal
        show={showPaymentModal}
        handleClose={() =>
          setShowPaymentModal(false)
        }
        values={paymentValues}
        price={promotionPrice}
        title={
          promotionType === "recommended"
            ? "Polecana TOP 3 w serwisie CARTENA"
            : "Wyróżnienie firmy w serwisie CARTENA"
        }
        handleSubmit={() => {
          handleSubmitPromotion();
        }}
      />
    </PageLayout>
  );
};

export default Promotion;